/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */
import { assign, createMachine, interpret, Interpreter, State } from 'xstate-v4'
import { Logger } from '../../../composables/useGtmLogRequest'
import { logger } from '../../../utility/logger'
import { getDefaultSearchFilters } from '../../search/defaults'
import { FetchCampsiteMapSearchService } from '../infrastructure/FetchCampsiteMapSearchService'
import { FetchCampsitePriceMapSearchService } from '../infrastructure/FetchCampsitePriceMapSearchService'
import { MapSearchContext, SearchFilters } from '../schemas'
import { generateSnappedGrids, Grid } from './grids'

const PAGE_SIZE = 20
const LARGE_LIMIT = 999999

interface MapSearchStateSchema {
  states: {
    idle: object
    searching: {
      states: {
        setting: object
        fetching: object
      }
    }
    results: object
    error: object
  }
}

interface GridResult {
  grid: Grid
  campsites: any[]
}

interface ExtendedMapSearchContext extends MapSearchContext {
  searchedGrids: { [key: string]: GridResult }
  keyedResults: { [key: string]: any[] }
  filtersKey: string
  signal?: AbortSignal
}

export type MapSearchStateEvent =
  | {
      type: 'search'
      filters: SearchFilters
      signal?: AbortSignal
      onlyBoundsChanged?: boolean
    }
  | {
      type: 'priceSearch'
      filters: SearchFilters
      signal?: AbortSignal
      onlyBoundsChanged?: boolean
    }
  | { type: 'RESULT_RECEIVED'; result: any[] }
  | { type: 'SEARCH_FAILED' }

export function returnMapSearchMachine() {
  return createMachine<MapSearchContext, MapSearchStateEvent>(
    {
      id: 'search',
      initial: 'idle',
      context: {
        filters: getDefaultSearchFilters(),
        page: 1,
        languageCode: 'en-gb',
        logger: undefined,
        campsites: [],
        keyedResults: {},
        searchedGrids: {},
        filtersKey: '',
        errorOccurred: false,
        within: undefined,
      },
      on: {
        search: 'searching',
        priceSearch: 'searching',
        SEARCH_FAILED: {
          actions: assign({
            errorOccurred: (_context, _event) => true,
          }),
        },
        RESULT_RECEIVED: {
          actions: assign((context, event) => {
            const key = context.filtersKey
            const existingCampsites = context.campsites
            const incomingCampsites = event.result
            const campsitesMap = new Map(
              existingCampsites.map((campsite) => [campsite.id, campsite]),
            )

            // Filter out any we already have

            incomingCampsites.forEach((campsite) => {
              if (!campsitesMap.has(campsite.id)) {
                campsitesMap.set(campsite.id, campsite)
              }
            })

            const updatedCampsites = Array.from(campsitesMap.values())
            const bounds = context.filters.bounds
            const filteredCampsites = bounds
              ? updatedCampsites.filter((campsite) => {
                  return (
                    campsite.lat <= bounds.northEast.lat &&
                    campsite.lat >= bounds.southWest.lat &&
                    campsite.lng <= bounds.northEast.lng &&
                    campsite.lng >= bounds.southWest.lng
                  )
                })
              : updatedCampsites

            return {
              ...context,
              campsites: filteredCampsites,
              keyedResults: key
                ? { ...context.keyedResults, [key]: updatedCampsites }
                : context.keyedResults,
            }
          }),
        },
      },
      states: {
        idle: {},
        searching: {
          initial: 'setting',
          states: {
            setting: {
              entry: assign({
                filters: (_, event) =>
                  (event.type === 'search' || event.type === 'priceSearch') &&
                  event.filters,
                results: (_) => undefined,
                campsites: (context, event) => {
                  if (
                    !(event.type === 'search' || event.type === 'priceSearch')
                  ) {
                    return
                  }
                  const bounds = event.filters.bounds
                  if (event.onlyBoundsChanged && bounds) {
                    logger('onlyBoundsChanged')
                    return context.campsites.filter((campsite) => {
                      return (
                        campsite.lat <= bounds.northEast.lat &&
                        campsite.lat >= bounds.southWest.lat &&
                        campsite.lng <= bounds.northEast.lng &&
                        campsite.lng >= bounds.southWest.lng
                      )
                    })
                  } else {
                    return []
                  }
                },
                page: (_) => 1,
                errorOccurred: (_) => false,

                signal: (_, { signal }) => signal,

                filtersKey: (_, { filters }) =>
                  JSON.stringify({
                    ...filters,
                    bounds: undefined,
                    base: undefined,
                    cleared: undefined,
                    limit: undefined,
                    page: undefined,
                    within: undefined,
                  }),
              }),
              always: 'fetching',
            },
            fetching: {
              invoke: {
                src: (context) => async (callback) => {
                  const { filters, languageCode, logger, signal } = context

                  if (signal.aborted) {
                    return Promise.resolve()
                  }
                  const isDatedSearch = Boolean(filters.dates)
                  const performSearch = async (
                    searchFilters,
                    gridKey?: string,
                  ) => {
                    if (gridKey && context.searchedGrids[gridKey]) {
                      return Promise.resolve(context.searchedGrids[gridKey])
                    }

                    if (isDatedSearch) {
                      const searchService =
                        new FetchCampsitePriceMapSearchService()

                      return await searchService.search(
                        searchFilters,
                        languageCode,
                        logger,
                        { timeout: 30000, signal },
                      )
                    } else {
                      const searchService = new FetchCampsiteMapSearchService()

                      return await searchService.search(
                        searchFilters,
                        languageCode,
                        logger,
                        { timeout: 30000, signal },
                      )
                    }
                  }

                  const searchManager = createLimitedConcurrencySearch(
                    8,
                    performSearch,
                    signal,
                  )

                  if (filters.bounds) {
                    const grids = generateSnappedGrids(filters.bounds)
                    grids.forEach((grid) => {
                      const gridKey = `${grid.bounds.southWest.lat},${grid.bounds.southWest.lng},${grid.bounds.northEast.lat},${grid.bounds.northEast.lng}:${context.filtersKey}`
                      const gridFilters = {
                        ...filters,
                        bounds: grid.bounds,
                        limit: LARGE_LIMIT,
                      }
                      searchManager.queue.push({
                        grid,
                        gridFilters,
                        context,
                        callback,
                        gridKey,
                      })
                    })
                    await searchManager.start()
                  } else {
                    // no bounds

                    const initialResult = await performSearch({
                      ...filters,
                      limit: isDatedSearch ? LARGE_LIMIT : PAGE_SIZE,
                      page: 1,
                    })

                    const totalCount = initialResult.count
                    const totalPages = Math.ceil(totalCount / PAGE_SIZE)
                    if (!isDatedSearch) {
                      for (let page = 2; page <= totalPages; page++) {
                        const pageFilters = {
                          ...filters,
                          limit: PAGE_SIZE,
                          page,
                        }
                        searchManager.queue.push({
                          grid: null,
                          gridFilters: pageFilters,
                          context,
                          callback,
                          gridKey: null,
                        })
                      }
                    }
                    callback({
                      type: 'RESULT_RECEIVED',

                      result: initialResult.campsites,
                    })
                    await searchManager.start()
                    // this will contain meta data like the point

                    return initialResult
                  }
                },
                onDone: [
                  {
                    target: '#search.results',
                    actions: ['setResults'],
                    cond: (_context, event) => !!event.data,
                  },
                  { target: '#search.results' },
                ],
                onError: [
                  {
                    target: '#search.idle',

                    cond: (_context, event) => event.data.cause?.code === 20,
                  },
                  { target: '#search.error' },
                ],
              },
            },
          },
        },
        results: {},
        error: {
          onEntry: ['setError'],
          onExit: ['clearError'],
        },
      },
    },
    {
      actions: {
        clearError: assign((_) => {
          return {
            errorMessage: undefined,
            errorOccurred: false,
          }
        }),

        setError: assign((_, { data }: any) => {
          return {
            errorMessage: data.toString(),
            errorOccurred: true,
          }
        }),

        setResults: assign((_, { data }: any) => {
          return {
            results: data,

            within: data?.within,
            // campsites: data.campsites,
          }
        }),

        logError: (context, event: any) => {
          console.error(
            'MapsError',
            JSON.stringify(event),
            JSON.stringify(context),
          )
        },
      },
      services: {
        // No additional services defined here as the logic is handled within the states
      },
    },
  )
}

function createLimitedConcurrencySearch(
  concurrencyLimit: number,
  performSearch: (filters: SearchFilters, gridKey?: string) => Promise<any>,
  signal: AbortSignal,
) {
  let active = 0
  let queue = []
  let resolveAll
  const allTasksDone = new Promise((resolve) => {
    resolveAll = resolve
  })
  const runNext = () => {
    if (signal.aborted) {
      queue = []
      active = 0
    }
    if (queue.length === 0) {
      resolveAll()
    }
    if (queue.length === 0 || active >= concurrencyLimit) {
      return
    }
    const { grid, gridFilters, context, callback, gridKey } = queue.shift()
    active++
    performSearch(gridFilters, gridKey)
      .then((result) => {
        if (signal.aborted) {
          return
        }

        const campsites = result?.campsites || []
        if (grid) {
          context.searchedGrids[gridKey] = { grid, campsites }
        }

        callback({ type: 'RESULT_RECEIVED', result: campsites })
      })
      .catch((error) => {
        if (error.name === 'AbortError' || error.cause?.name === 'AbortError') {
          return
        }

        callback({ type: 'SEARCH_FAILED' })
      })
      .finally(() => {
        active--
        requestIdleCallback(() => runNext())
      })
    requestIdleCallback(() => runNext())
  }
  const start = () => {
    runNext()
    return allTasksDone
  }

  return { queue, runNext, start }
}

export type MapSearchMachineInterpreter = Interpreter<
  ExtendedMapSearchContext,
  MapSearchStateSchema,
  MapSearchStateEvent
>

export type MapSearchState = State<
  ExtendedMapSearchContext,
  MapSearchStateEvent
>

export function getMapSearchMachineInterpreted(
  languageCode: string,
  logger: Logger,
) {
  const machine = returnMapSearchMachine()
  return interpret(
    machine.withContext({
      ...machine.context,
      languageCode,
      logger,
    }),
  )
}
